/**
 * TypeScript interfaces for the internal advertising system
 * Provides type safety for ad formats, placements, tracking, and campaign management
 */

/**
 * Ad format types
 */
export enum AdFormat {
  IMAGE = "image",
  VIDEO = "video",
  TEXT = "text",
  CAROUSEL = "carousel",
  NATIVE = "native",
}

/**
 * Ad placement locations throughout the application
 */
export enum AdPlacement {
  HEADER = "header",
  SIDEBAR = "sidebar",
  POPUP = "popup",
  INTERSTITIAL = "interstitial",
  IN_FEED = "in-feed",
  FOOTER = "footer",
}

/**
 * Ad position within the page
 */
export enum AdPosition {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  MIDDLE = "middle",
  IN_CONTENT = "in-content",
}

/**
 * Target page for the ad
 */

export enum AdPage {
  HOME = "home",
  PROPERTIES = "properties",
  PROPERTY_DETAIL = "property-detail",
  DEVELOPERS = "developers",
  REALTORS = "realtors",
  //BLOG = 'blog', Removing this, we well not be using this page anymore.
  MANAGED_PROPERTIES = "managed-properties",
  MANAGED_DEVELOPMENTS = "managed-developments",
  WEEKLY_DEALS = "weekly-deals",
  // Add more pages as needed
  REQUEST_PROPERTY = "request-property",
  CONTACT = "contact",
}

/**
 * Ad campaign status
 */
export enum AdStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  PAUSED = "paused",
  COMPLETED = "completed",
  ARCHIVED = "archived",
}

export enum AdReviewStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
}

export enum AdPaymentStatus {
  PENDING = "pending",
  PAID = "paid",
  FAILED = "failed",
  REFUNDED = "refunded",
}

/**
 * Main advertisement data structure
 */
export interface Advertisement {
  id: string;
  title: string;
  description?: string;
  format: AdFormat;
  placement: AdPlacement;
  position: AdPosition;
  page: AdPage;
  imageUrl?: string;
  videoUrl?: string;
  targetUrl: string;
  altText?: string;
  campaignId?: string;
  creator: {
    full_name: string;
    email: string;
  };
  priority: number;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  advertiserName?: string;
  dimensions?: {
    width: number;
    height: number;
  };
  metadata?: Record<string, unknown>;

  reviewStatus: AdReviewStatus;
  rejectionReason?: string;
  paymentStatus: AdPaymentStatus;
  paymentReference?: string;
  createdByAdmin: boolean;
}

/**
 * Ad tracking data for impressions
 */
export interface AdImpression {
  id?: string;
  adId: string;
  placement: AdPlacement;
  timestamp: string;
  userId?: string;
  sessionId?: string;
  pageUrl: string;
  referrer?: string;
  userAgent?: string;
  deviceType?: "desktop" | "mobile" | "tablet";
  metadata?: Record<string, unknown>;
}

/**
 * Ad tracking data for clicks
 */
export interface AdClick {
  id?: string;
  adId: string;
  placement: AdPlacement;
  timestamp: string;
  userId?: string;
  sessionId?: string;
  pageUrl: string;
  targetUrl: string;
  referrer?: string;
  userAgent?: string;
  deviceType?: "desktop" | "mobile" | "tablet";
  metadata?: Record<string, unknown>;
}

/**
 * Combined ad tracking data
 */
export interface AdTracking {
  impressions: AdImpression[];
  clicks: AdClick[];
  totalImpressions: number;
  totalClicks: number;
  clickThroughRate: number;
  uniqueImpressions?: number;
  uniqueClicks?: number;
}

/**
 * Ad campaign data structure
 */
export interface AdCampaign {
  id: string;
  name: string;
  description?: string;
  status: AdStatus;
  budget?: number;
  spent?: number;
  startDate: string;
  endDate?: string;
  targetAudience?: {
    locations?: string[];
    demographics?: string[];
    interests?: string[];
  };
  ads: Advertisement[];
  analytics: AdCampaignAnalytics;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;

  reviewStatus: AdReviewStatus;
  rejectionReason?: string;
  paymentStatus: AdPaymentStatus;
  paymentReference?: string;
  createdByAdmin: boolean;
}

/**
 * Campaign analytics data
 */
export interface AdCampaignAnalytics {
  impressions: number;
  clicks: number;
  clickThroughRate: number;
  conversions?: number;
  conversionRate?: number;
  reach?: number;
  engagement?: number;
  costPerClick?: number;
  costPerImpression?: number;
  roi?: number;
}

/**
 * Ad placement query parameters
 */
export interface AdPlacementQuery {
  placement: AdPlacement;
  position?: AdPosition;
  page?: AdPage;
  limit?: number;
  userId?: string;
}

/**
 * Ad campaign query parameters
 */
export interface AdCampaignQuery {
  status?: AdStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sortBy?: "createdAt" | "name" | "status" | "budget";
  sortOrder?: "asc" | "desc";
  reviewStatus?: AdReviewStatus;
}

/**
 * Ad campaign creation/update payload
 */
export interface AdCampaignPayload {
  name: string;
  description?: string;
  status: AdStatus;
  budget?: number;
  startDate: string;
  endDate?: string;
  targetAudience?: {
    locations?: string[];
    demographics?: string[];
    interests?: string[];
  };
}

/**
 * Advertisement creation/update payload
 */
export interface AdvertisementPayload {
  title: string;
  description?: string;
  format: AdFormat;
  placement: AdPlacement;
  position?: AdPosition;
  page?: AdPage;
  imageUrl?: string;
  videoUrl?: string;
  targetUrl: string;
  altText?: string;
  campaignId?: string;
  priority?: number;
  startDate: string;
  endDate?: string;
  isActive?: boolean;
  dimensions?: {
    width: number;
    height: number;
  };
  metadata?: Record<string, unknown>;
}

/**
 * Ad analytics response
 */
export interface AdAnalyticsResponse {
  adId: string;
  campaignId?: string;
  dateRange: {
    start: string;
    end: string;
  };
  metrics: {
    impressions: number;
    clicks: number;
    clickThroughRate: number;
    uniqueImpressions?: number;
    uniqueClicks?: number;
  };
  breakdown?: {
    byDate?: Record<string, { impressions: number; clicks: number }>;
    byPlacement?: Record<string, { impressions: number; clicks: number }>;
    byDevice?: Record<string, { impressions: number; clicks: number }>;
  };
}

/**
 * Pagination data for ad lists
 */
export interface AdPaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Ad list response with pagination
 */
export interface AdListResponse {
  ads: Advertisement[];
  pagination: AdPaginationData;
}

/**
 * Campaign list response with pagination
 */
export interface CampaignListResponse {
  campaigns: AdCampaign[];
  pagination: AdPaginationData;
}

/**
 * Page-level ad response grouped by placement
 */
export type PageAdsResponse = Record<string, Advertisement[]>;

export interface RejectAdPayload {
  rejectionReason: string;
}

// Response type matching your backend day-by-day calendar mapping
export type BookedSlotsCalendarResponse = Record<string, Advertisement[]>;

export type DimensionStatus = "valid" | "warning" | "invalid" | "unknown";
